import React from 'react';

import i18n from '@yandex-int/i18n';
import * as keyset from 'i18n/Settings';

import { Form } from '../blocks/form/Form';
import { NavigationBlocker } from '../blocks/NavigationBlocker/NavigationBlocker';
import { assignField, getKeys } from '../utils';
import { getDisplayName } from '../utils/hocs';

import {
    FormAdditionalProps,
    FormChangeHandler,
    FormContainerOptions,
    FormErrors,
    FormSetStateAndValidate,
    FormState,
    FormStateHandlers,
    InjectedFormProps,
    ValidationSchema,
} from './types';

const t = i18n(keyset);

export interface FormContainerProps {
    registerChanges: (hasChanges: boolean) => void;
    hasChanges: boolean;
}

const unsavedAlertMessage = t('alert-unsave-message');
const allowedAnchorsToJump = ['#brandVerificationWebsite'];

const formContainer = <OuterProps, Values>({
    mapPropsToValues,
    setupValidationSchema,
    handleSubmit,
    showPrompt = true,
}: FormContainerOptions<OuterProps, Values>) => {
    return (Component: React.ComponentType<InjectedFormProps<Values, OuterProps>>) => {
        return class FormContainer extends React.Component<OuterProps & FormContainerProps, FormState<Values>> {
            public displayName = getDisplayName(Component);

            private formRef = React.createRef<HTMLFormElement>();
            private validationSchema: ValidationSchema<Values>;

            constructor(props: OuterProps & FormContainerProps) {
                super(props);

                this.state = {
                    values: mapPropsToValues(props),
                    errors: {},
                    touched: {},
                    isSubmitting: false,
                    isNonValidationError: false,
                };

                this.validationSchema = setupValidationSchema ? setupValidationSchema(props, this.state) : {};
            }

            public componentDidMount() {
                window.onbeforeunload = this.onBeforeUnload;
            }

            public componentWillUnmount() {
                window.onbeforeunload = null;

                this.props.registerChanges(false);
            }

            public handleSubmit = async (e?: React.FormEvent) => {
                e && e.preventDefault();

                try {
                    await new Promise((resolve) => this.prepareSubmission(true, resolve));
                    await handleSubmit(this.state.values, this.props, this.stateHandlers);

                    this.props.registerChanges(false);
                    this.refresh();
                } catch (_err) {
                    if (this.isValid) {
                        this.setState({
                            isNonValidationError: true,
                        });
                    }

                    return;
                } finally {
                    this.setState({ isSubmitting: false });
                }
            };

            public handleSaveDraft = async () => {
                try {
                    await new Promise((resolve) => this.prepareSubmission(true, resolve));
                    await handleSubmit(this.state.values, this.props, this.stateHandlers);

                    this.props.registerChanges(false);
                    this.refresh();
                } finally {
                    this.setState({ isSubmitting: false });
                }
            };

            public render() {
                return (
                    <Form onSubmit={this.handleSubmit} innerRef={this.formRef}>
                        {showPrompt && (
                            <NavigationBlocker
                                message={(location) =>
                                    window.location.pathname === location.pathname ||
                                    allowedAnchorsToJump.includes(location.hash) ||
                                    unsavedAlertMessage
                                }
                                when={this.props.hasChanges}
                            />
                        )}
                        <Component
                            {...this.props}
                            {...this.state}
                            {...this.additionalProps}
                            {...this.stateHandlers}
                            handleSaveDraft={this.handleSaveDraft}
                        />
                    </Form>
                );
            }

            private get isValid() {
                const errors = this.state.errors;
                const nonFalsyErrorsCount = getKeys(errors).reduce((acc, key) => {
                    const error = errors[key];

                    return acc + Number(error instanceof Array ? error.some(Boolean) : Boolean(errors[key]));
                }, 0);

                return nonFalsyErrorsCount === 0;
            }

            private get additionalProps(): FormAdditionalProps {
                return {
                    isValid: this.isValid,
                    isNonValidationError: this.state.isNonValidationError,
                    formElement: this.formRef.current!,
                };
            }

            private get stateHandlers(): FormStateHandlers<Values> {
                return {
                    onChange: this.onChange,
                    setErrors: this.setErrors,
                    prepareSubmission: this.prepareSubmission,
                    onCheckboxChange: this.onCheckboxChange,
                    onUploaderChange: this.onUploaderChange,
                    setStateAndValidate: this.setStateAndValidate,
                };
            }

            private onChange: FormChangeHandler<Values> = (field) => (value) => {
                this.props.registerChanges(true);

                this.setState((prevState) => {
                    const newErrors = this.getNewErrors(field, value);
                    const newValues = Object.assign({}, prevState.values, { [field]: value });
                    const newTouched = Object.assign({}, prevState.touched, { [field]: true });

                    return {
                        values: newValues,
                        errors: newErrors,
                        touched: newTouched,
                    };
                });
            };

            private getNewErrors = <K extends keyof Values>(field: K, value: Values[typeof field]) => {
                const { errors } = this.state;
                const validator = this.validationSchema[field];
                // Если валидатор не установлен, то сбрасываем ошибку для изменяемого поля
                const errorsCausedByFieldChange = validator ? validator(value, this.state) : { [field]: '' };
                return Object.assign({}, errors, errorsCausedByFieldChange);
            };

            private onUploaderChange = (value: string | null, error?: string) => {
                this.props.registerChanges(true);

                this.setState((prevState) => ({
                    values: Object.assign({}, prevState.values, { logoId: value }),
                    errors: Object.assign({}, prevState.errors, { logoId: error }),
                    touched: Object.assign({}, prevState.touched, { logoId: true }),
                }));
            };

            private onCheckboxChange = (field: keyof Values) => () => {
                this.props.registerChanges(true);

                this.setState((prevState) => ({
                    values: assignField(prevState.values, field, !prevState.values[field] as Values[keyof Values]),
                    touched: assignField(prevState.touched, field, true),
                    errors: this.getNewErrors(field, !prevState.values[field] as Values[keyof Values]),
                }));
            };
            private setErrors = (errors: FormErrors<Values & {}>, cb?: () => unknown) => {
                this.setState({ errors }, cb);
            };

            private prepareSubmission = (isSubmitting: boolean, cb?: (value?: unknown) => void) => {
                this.setState({ isSubmitting, isNonValidationError: false }, cb);
            };

            private setStateAndValidate: FormSetStateAndValidate<Values> = ({ errors, values }, fieldsToValidate) => {
                this.props.registerChanges(true);

                this.setState((prevState) => {
                    const resolvedErrors = errors ?? prevState.errors;
                    const resolvedValues = values ?? prevState.values;

                    const newErrors = fieldsToValidate.reduce<FormErrors<Values & {}>>(
                        (prevErrors, field) => {
                            const validator = this.validationSchema[field];
                            const errorsCausedByFieldChange = validator
                                ? validator(resolvedValues[field], prevState)
                                : { [field]: '' };

                            return Object.assign({}, prevErrors, errorsCausedByFieldChange);
                        },
                        Object.assign({}, resolvedErrors),
                    );

                    return {
                        values: resolvedValues,
                        errors: newErrors,
                    };
                });
            };

            private refresh = () => {
                this.setState({
                    touched: {},
                    errors: {},
                    isNonValidationError: false,
                });
            };

            private onBeforeUnload = () => {
                if (this.props.hasChanges) {
                    return unsavedAlertMessage;
                }
            };
        };
    };
};

export default formContainer;
